import {
  chainValidations,
  type ValidateFunction,
  validateMaxLength,
  validateMinLength,
  validatePattern,
  type ValidationReturn,
} from 'formtastisch'
import { type IntlShape } from 'react-intl'

import {
  getMaxLengthErrorMessage,
  getMinLengthErrorMessage,
  validateEmail,
  validateNotEmpty,
  validateRequired,
} from '../base-form-handling'

import { loginRegistrationIsAllowedCharactersForName } from './loginRegistrationIsAllowedCharactersForName'

const MAX_COMPANY_LENGTH = 50
const MAX_NAME_LENGTH = 25
const MIN_PASSWORD_LENGTH = 6
const MIN_PHONE_LENGTH = 5
const PHONE_REGEX_DIGITS = /^\d+$/u
export const MAX_PASSWORD_LENGTH = 512
export const validateCheckBoxChecked: ValidateFunction = (
  value: boolean,
  message: string,
): ValidationReturn => {
  if (!value) {
    return { errors: message, valid: false }
  }

  return { valid: true }
}

export const loginRegistrationRegistrationFormValidatePhoneNumber: ValidateFunction =
  (value: string, intl: IntlShape): ValidationReturn => {
    if (value) {
      const trimmedPhoneNumber = value.replaceAll(/[ +]/gu, '')

      return chainValidations(
        () =>
          validateMinLength(
            trimmedPhoneNumber,
            MIN_PHONE_LENGTH,
            intl.formatMessage(
              { id: 'form.error.input.minLength' },
              { minLength: MIN_PHONE_LENGTH },
            ),
          ),
        () =>
          validatePattern(
            trimmedPhoneNumber,
            PHONE_REGEX_DIGITS,
            intl.formatMessage({
              id: 'registrationForm.messages.validation.phone',
            }),
          ),
      )
    }

    return { valid: true }
  }

const validateForAllowedCharacters: ValidateFunction = (
  value: string,
  message: string,
): ValidationReturn => {
  if (!loginRegistrationIsAllowedCharactersForName(value)) {
    return { errors: message, valid: false }
  }

  return { valid: true }
}

export const loginRegistrationRegistrationFormValidateEmail = (
  value: string,
  intl: IntlShape,
): ValidationReturn => {
  return chainValidations(
    () =>
      validateRequired(
        value,
        intl.formatMessage({ id: 'form.error.input.missing' }),
      ),
    () =>
      validateEmail(
        value,
        intl.formatMessage(
          {
            id: 'registrationForm.messages.validation.email',
          },
          { emailValue: value },
        ),
      ),
  )
}

export const loginRegistrationRegistrationFormValidateName = (
  value: string,
  intl: IntlShape,
): ValidationReturn => {
  return chainValidations(
    () =>
      validateNotEmpty(
        value,
        intl.formatMessage({ id: 'form.error.input.missing' }),
      ),
    () =>
      validateMaxLength(
        value,
        MAX_NAME_LENGTH,
        getMaxLengthErrorMessage(intl, MAX_NAME_LENGTH),
      ),
    () =>
      validateForAllowedCharacters(
        value,
        intl.formatMessage({
          id: 'registrationForm.messages.validation.name.invalid',
        }),
      ),
  )
}

export const loginRegistrationRegistrationFormValidatePassword = (
  value: string,
  intl: IntlShape,
  email?: string,
): ValidationReturn => {
  return chainValidations(
    () =>
      validateRequired(
        value,
        intl.formatMessage({ id: 'form.error.input.missing' }),
      ),
    () =>
      validateMinLength(
        value,
        MIN_PASSWORD_LENGTH,
        getMinLengthErrorMessage(intl, MIN_PASSWORD_LENGTH),
      ),
    () =>
      validateMaxLength(
        value,
        MAX_PASSWORD_LENGTH,
        getMaxLengthErrorMessage(intl, MAX_PASSWORD_LENGTH),
      ),
    (): ValidationReturn => {
      //WSBEARS-513 - Email should not match with password.
      if (value === email) {
        return {
          errors: intl.formatMessage({
            id: 'registration.setPassword.emailError',
          }),
          valid: false,
        }
      }

      return {
        valid: true,
      }
    },
  )
}

export const loginRegistrationRegistrationFormValidateCompanyName = (
  value: string,
  intl: IntlShape,
): ValidationReturn => {
  return chainValidations(
    () =>
      validateNotEmpty(
        value,
        intl.formatMessage({ id: 'form.error.input.missing' }),
      ),
    () =>
      validateMaxLength(
        value,
        MAX_COMPANY_LENGTH,
        getMaxLengthErrorMessage(intl, MAX_COMPANY_LENGTH),
      ),
    () =>
      validateForAllowedCharacters(
        value,
        intl.formatMessage({
          id: 'registrationForm.messages.validation.name.invalid',
        }),
      ),
  )
}

export const loginRegistrationRegistrationFormValidateVatNumber = (
  value: string,
  intl: IntlShape,
): ValidationReturn => {
  return validateNotEmpty(
    value,
    intl.formatMessage({ id: 'form.error.input.missing' }),
  )
}
